import * as React from 'react'
import type { Dispatch } from 'redux'
import { connect } from 'react-redux'
import {
  BGImageContainer,
  GradientContainer,
  QuizBGContainer,
  QuizBodyText,
  QuizButton,
  QuizHeaderText,
  QuizTopContainer
} from '@components/quiz/quizStyles'
import type { AppStateType } from '../../../reducers/appstate'
import { DipseaContentfulAssets } from '@components/shared'
import { FadeInImage } from '@components/shared/FadeInImage'
import { Container, Col, Row, Hidden } from 'react-awesome-styled-grid'
import { isUserExpiring } from '@components/subscribe/BillingInformation/selectors'
import { openHome } from '../../../actions/navigationActions'
import { clearPromoCode } from '../../../actions/subscriptionActions'
import { showAuthModal } from '../../../actions/userActions'
import {
  togglePromoModal,
  setHeaderCopyForAuthAndSubscribeModals
} from '../../../actions/promoActions'
import Quiz from '@components/quiz/flow1/Quiz'
import type { SubscriptionLengthType, QuizType } from '../../../flowTypes'
import { events } from '../../../services/analytics'
import { dismissIntercom } from '@services/intercom'
import { useRouter } from 'next/router'
import { isEmpty } from 'lodash'
import BackgroundGradient from '@components/BackgroundGradient'

type PropsType = {
  quizType: QuizType,
  isSignedIn: boolean,
  modalOpen: boolean,
  existingResult?: string,
  existingGenderPreference?: string,
  isExpiring: boolean,
  plan?: SubscriptionLengthType
}

type DispatchPropsType = {
  openAuthModal: () => void,
  navigateToHome: () => void,
  openSubscribe: () => void,
  showAuthModal: () => void,
  clearPromo: () => void,
  setQuizCopy: (
    textAuthHeader: string,
    textAuthSubheader: string,
    textSubscriptionConfirmationHeader: string,
    textSubscriptionConfirmationSubheader: string
  ) => void
}

function QuizFlow1 (props: PropsType & DispatchPropsType): React.ReactNode {
  const isIntercomVisible = typeof window !== 'undefined' && window.Intercom
  const router = useRouter()
  const searchParams = new URLSearchParams(router.asPath.split(/\?/)[1])
  const query: Record<string, any> = {}
  for (const [key, value] of searchParams) {
    query[key] = value
  }
  // static site generation won't pull the query object for us, we have to manually create it
  router.query = query
  React.useEffect((): void => {
    if (isIntercomVisible) {
      dismissIntercom()
    }
  }, [isIntercomVisible])

  const [isQuizInProgress, setQuizInProgress] = React.useState(false)
  React.useEffect((): (() => void) => {
    if (props.existingResult || !isEmpty(router.query)) {
      setQuizInProgress(true)
    }
    return (): void => {
      props.clearPromo()
    }
  }, [])

  let mobileIntroImage = DipseaContentfulAssets['Quiz_LandingPage_HeroImage_Mobile_Smaller.jpg']
  let desktopIntroImage = DipseaContentfulAssets['Quiz_LandingPage_HeroImage_Desktop_Tablet_Smaller.jpg']
  let headerText = `Sexy Audio Stories With Characters You'll Love`
  let descriptionText = `We've got hundreds of sexy stories starring straight and queer hunks. Answer a few questions to find the perfect character to get you started.`

  const textAuthHeader = 'Listen for free'
  const textAuthSubheader = 'Start a free trial and unlock our endless library of audio stories, bingeable books, sleep scenes, and wellness sessions'
  const textSubscriptionConfirmationHeader = 'You’ve Just Unlocked a Sensual Universe.'
  let textSubscriptionConfirmationSubheader = 'Now log into the app to listen to your character match, plus hundreds of sexy stories, sleep scenes, and wellness sessions.'

  if (props.quizType === 'trope-quiz') {
    mobileIntroImage = DipseaContentfulAssets.trope_quiz_intro_mobile
    desktopIntroImage = DipseaContentfulAssets.trope_quiz_intro_desktop
    headerText = `Sexy Audio Stories With Themes You'll Love`
    descriptionText = `We've got hundreds of sexy stories with your favorite romance tropes. Answer a few questions to find the perfect story to get you started.`
    textSubscriptionConfirmationSubheader = 'Log into the app to listen to your result, plus hundreds of sexy stories, sleep scenes, and wellness sessions.'
  } else if (props.quizType === 'fantasy-quiz') {
    mobileIntroImage = DipseaContentfulAssets.trope_quiz_intro_mobile
    desktopIntroImage = DipseaContentfulAssets.trope_quiz_intro_desktop
    headerText = `Get lost in a steamy fantasy world...`
    descriptionText = `Take this short quiz and get matched with your perfect Dipsea series. We've got hundreds of sexy stories designed to turn you on, whatever your fantasy is.`
    textSubscriptionConfirmationSubheader = 'Log into the app to listen to your result, plus hundreds of sexy stories, sleep scenes, and wellness sessions.'
  }

  const startQuiz = (): void => {
    events.cta_button_clicked({
      cta_name: 'take_the_quiz',
      url: (window.location || {}).href
    })

    props.setQuizCopy(
      textAuthHeader,
      textAuthSubheader,
      textSubscriptionConfirmationHeader,
      textSubscriptionConfirmationSubheader
    )

    setQuizInProgress(true)
  }

  return (
    <BGImageContainer>
      {!isQuizInProgress && (
        <QuizBGContainer className='landing'>
          <Hidden xs={false} sm={false} md={true} lg={true} xl={true}>
            <FadeInImage
              objectFit='cover'
              layout='fill'
              fadeInTime={0.3}
              src={mobileIntroImage}
              aria-hidden='true'
            />
          </Hidden>
          <Hidden xs={true} sm={true} md={false} lg={false} xl={false}>
            <FadeInImage
              objectFit='cover'
              layout='fill'
              fadeInTime={0.3}
              src={desktopIntroImage}
              aria-hidden='true'
            />
          </Hidden>
        </QuizBGContainer>
      )}
      {isQuizInProgress && (
        <QuizBGContainer>
        <BackgroundGradient quadrant={1} />
        </QuizBGContainer>
      )}
      <div>
        {isQuizInProgress
          ? (
          <Quiz
            isSignedIn={props.isSignedIn}
            existingResult={props.existingResult || (router.query.result as string)}
            existingGenderPreference={props.existingGenderPreference}
            quizType={props.quizType}
          />
            )
          : (
          <QuizTopContainer isSignedIn={props.isSignedIn}>
            <GradientContainer>
              <Container>
                <Row align='center'>
                  <Col
                    xs='4'
                    sm='8'
                    md='6'
                    lg='7'
                    xl='6'
                    offset={{ md: 1, lg: 0, xl: 1 }}
                    align={{ md: 'center', lg: 'flex-start' }}
                  >
                    <QuizHeaderText>{headerText}</QuizHeaderText>
                    <QuizBodyText>{descriptionText}</QuizBodyText>
                    <QuizButton onClick={startQuiz}>TAKE THE QUIZ</QuizButton>
                  </Col>
                </Row>
              </Container>
            </GradientContainer>
          </QuizTopContainer>
            )}
      </div>
    </BGImageContainer>
  )
}

const mapStateToProps = (
  state: AppStateType,
  ownProps: { quizType: QuizType }
): PropsType => {
  return {
    quizType: ownProps.quizType,
    isSignedIn: !!(state.user || {}).id,
    modalOpen: !!state.promoFlowModalOpen,
    existingResult: state.quizResult,
    existingGenderPreference: state.quizGenderPreference,
    isExpiring: isUserExpiring(state),
    plan: state.subscriptionPurchase.plan
  } as PropsType
}

const mapDispatchToProps = (dispatch: Dispatch<any>): DispatchPropsType => {
  return {
    openAuthModal: (): void => dispatch(showAuthModal(true, true)),
    navigateToHome: (): void => dispatch(openHome()),
    openSubscribe: (): void => dispatch(togglePromoModal()),
    showAuthModal: (): void => dispatch(showAuthModal(true)),
    clearPromo: (): void => dispatch(clearPromoCode()),
    setQuizCopy: (
      textAuthHeader: string,
      textAuthSubheader: string,
      textSubscriptionConfirmationHeader: string,
      textSubscriptionConfirmationSubheader: string
    ): void => {
      dispatch(
        setHeaderCopyForAuthAndSubscribeModals(
          textAuthHeader,
          textAuthSubheader,
          textSubscriptionConfirmationHeader,
          textSubscriptionConfirmationSubheader
        )
      )
    }
  } as DispatchPropsType
}

export default connect(mapStateToProps, mapDispatchToProps)(QuizFlow1)
